import { BRAND, hasModule, logoWidth } from "./lib/config";
import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "./components/Icon";
import { signOut, useSession, type SignedInUser } from "./lib/auth";
import { getProfile, newLeads } from "./lib/data";
import { useHash } from "./lib/route";
import { COUNTS_EVENT } from "./lib/events";
import { humanise } from "./lib/format";
import type { CountKey, Counts, RouteDef } from "./lib/nav";
import type { Profile, UserRole } from "./lib/supabase";
import { FIELD_COUNT_LABEL, FIELD_ROUTES_FIRST, FIELD_ROUTES_HIDDEN, FIELD_ROUTES_MAIN, FIELD_ROUTES_MORE, FieldApp, loadFieldCounts, usesFieldApp } from "./modules/field";
import { myCrewRecord } from "./modules/field/field-data";
import { MyShiftsLink } from "./modules/field/MyShiftsScreen";
import { Crm } from "./screens/Crm";
import { Customers } from "./screens/Customers";
import { openFeedbackCount } from "./lib/customers-data";
import { Integrations } from "./screens/Integrations";
import { Leads } from "./screens/Leads";
import { Login } from "./screens/Login";
import { Today } from "./screens/Today";
import { HelpHost, HelpScreen, ReportButton } from "./help/Help";
import { HELP, reportsOn, setHelpViewer } from "./help/data";

const ROUTES = {
  "#/today": { label: "Today", icon: "today", Screen: Today, module: "today" },
  ...FIELD_ROUTES_FIRST,
  "#/leads": { label: "Leads", icon: "inbox", Screen: Leads, count: "newLeads", module: "leads" },
  ...FIELD_ROUTES_MAIN,
  "#/customers": { label: "Customers", icon: "users", Screen: Customers, count: "openFeedback", hint: "Customer portal", module: "customers" },
  // Directors only: the API's /admin/crm routes and the outbox's RLS allow no other role.
  "#/crm": { label: "CRM", icon: "sync", Screen: Crm, hint: "Sync status", module: "crm", roles: ["director"] },
  ...FIELD_ROUTES_MORE,
  "#/integrations": { label: "Xero", icon: "external", Screen: Integrations, hint: "Accounting", module: "integrations" },
  // Every staff role: guides for each screen and "Something's not working".
  "#/help": { label: "Help", icon: "help", Screen: HelpScreen, hint: "Guides and reports" },
  ...FIELD_ROUTES_HIDDEN,
} satisfies Record<string, RouteDef>;
type Route = keyof typeof ROUTES;
// The Xero screen is only listed when the client layer switches Xero on (features.xero).
// Screens outside the client's portal modules are neither listed nor reachable by URL.
// Help is listed when the client layer has guides or reports are on.
const isEnabled = (r: Route) => {
  const m = (ROUTES[r] as RouteDef).module;
  return m ? hasModule(m) : r !== "#/help" || HELP.pages.length > 0 || reportsOn();
};
const ROUTE_KEYS = (Object.keys(ROUTES) as Route[]).filter((r) => isEnabled(r) && !(ROUTES[r] as RouteDef).hidden);
const isRoute = (h: string): h is Route => h in ROUTES && isEnabled(h as Route);
/** Screens limited to some roles (RouteDef.roles) are neither listed nor reachable for the others. */
const allowedFor = (r: Route, role: UserRole | undefined) => {
  const roles = (ROUTES[r] as RouteDef).roles;
  return !roles || (!!role && roles.includes(role));
};
const COUNT_LABEL: Partial<Record<CountKey, (n: number) => string>> = {
  newLeads: (n) => `${n} new`,
  openFeedback: (n) => (__OPS_PORTAL__.customerRequests ? `${n} feedback and requests to answer` : `${n} feedback to answer`),
  ...FIELD_COUNT_LABEL,
};

/** The screen for the current hash: "#/jobs/123" is the Jobs screen. */
function useHashRoute(role: UserRole | undefined): Route {
  const hash = useHash();
  const base = `#/${hash.replace(/^#\/?/, "").split("/")[0] ?? ""}`;
  return isRoute(base) && allowedFor(base, role) ? base : "#/today";
}


function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}

function useNavCounts(route: Route): Counts {
  const [counts, setCounts] = useState<Counts>({});
  const load = useCallback(() => {
    // Counts are a convenience: if a query fails the badge is simply left out.
    newLeads(1).then((r) => setCounts((c) => ({ ...c, newLeads: r.count })), () => undefined);
    loadFieldCounts(hasModule, setCounts);
    if (hasModule("customers")) openFeedbackCount().then((n) => setCounts((c) => ({ ...c, openFeedback: n })), () => undefined);
  }, []);
  useEffect(load, [load, route]);
  useEffect(() => {
    window.addEventListener(COUNTS_EVENT, load);
    return () => window.removeEventListener(COUNTS_EVENT, load);
  }, [load]);
  return counts;
}

const RAIL_KEY = "ops-portal:rail";
function readRailPref(): boolean | null {
  try {
    const v = window.localStorage.getItem(RAIL_KEY);
    return v === null ? null : v === "1";
  } catch {
    return null;
  }
}

export function App() {
  const user = useSession();
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined);
  useEffect(() => {
    if (!user) return setProfile(undefined);
    let live = true;
    getProfile(user.id).then((p) => live && setProfile(p), () => live && setProfile(null));
    return () => { live = false; };
  }, [user?.id]);
  if (user === undefined) return null;
  if (!user) return <Login />;
  if (profile === undefined) return null;
  // Modules can give some roles their own app (field-services: crew logins); everyone else gets the portal.
  if (usesFieldApp(profile)) {
    setHelpViewer(profile?.role, user.id);
    return <FieldApp user={user} profile={profile!} />;
  }
  return <Shell user={user} initialProfile={profile} />;
}

function Shell({ user, initialProfile }: { user: SignedInUser; initialProfile: Profile | null }) {
  setHelpViewer(initialProfile?.role, user.id);
  const route = useHashRoute(initialProfile?.role);
  const isMobile = useMedia("(max-width: 719px)");
  const isTablet = useMedia("(max-width: 1099px)");
  const [railPref, setRailPref] = useState<boolean | null>(readRailPref);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const profile = initialProfile;
  const counts = useNavCounts(route);
  // Staff who also work on site (login linked to a crew record) get "My shifts".
  const [linked, setLinked] = useState(false);
  useEffect(() => {
    let live = true;
    myCrewRecord(user.id).then((m) => live && setLinked(Boolean(m)), () => undefined);
    return () => { live = false; };
  }, [user.id]);
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const lastRoute = useRef(route);

  const rail = !isMobile && (railPref ?? isTablet);

  // Route change: close the drawer and move focus to the new page title.
  useEffect(() => {
    setDrawerOpen(false);
    if (lastRoute.current === route) return;
    lastRoute.current = route;
    window.scrollTo(0, 0);
    requestAnimationFrame(() => document.getElementById("page-title")?.focus({ preventScroll: true }));
  }, [route]);

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!drawerOpen) return;
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        menuButton.current?.focus();
      } else if (e.key === "Tab") {
        // Keep keyboard focus inside the open drawer.
        const items = document.getElementById("sidebar")?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])");
        if (!items?.length) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("no-scroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
    };
  }, [drawerOpen]);

  function toggleRail() {
    const next = !rail;
    setRailPref(next);
    try {
      window.localStorage.setItem(RAIL_KEY, next ? "1" : "0");
    } catch {
      /* per-viewer convenience only */
    }
  }

  const { Screen } = ROUTES[route];
  const name = profile?.full_name || user.email;
  const shellClass = ["shell", rail && "shell--rail", isMobile && "shell--mobile", drawerOpen && "shell--drawer-open"].filter(Boolean).join(" ");

  return (
    <div className={shellClass}>
      <button type="button" className="skip-link" onClick={() => document.getElementById("main")?.focus()}>
        Skip to content
      </button>

      {isMobile && (
        <div className="mobilebar">
          <button
            ref={menuButton}
            type="button"
            className="icon-btn icon-btn--dark"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="sidebar"
            onClick={() => setDrawerOpen(true)}
          >
            <Icon name="menu" size={24} />
            {(counts.newLeads ?? 0) + (counts.missingTickets ?? 0) > 0 && <span className="icon-btn__dot" aria-hidden="true" />}
          </button>
          <a href="#/today" className="mobilebar__logo">
            <img src={BRAND.logoSmall} alt={`${BRAND.name}, today`} width={logoWidth(41)} height="41" />
          </a>
          <span className="mobilebar__title">{ROUTES[route].label}</span>
        </div>
      )}

      <aside
        id="sidebar"
        className="side"
        aria-label="Portal navigation"
        inert={isMobile && !drawerOpen}
        {...(isMobile ? { role: "dialog", "aria-modal": drawerOpen } : {})}
      >
        <div className="side__top">
          <a href="#/today" className="side__logo" title={rail ? BRAND.name : undefined}>
            <img
              src={BRAND.logoSmall}
              srcSet={`${BRAND.logoSmall} 320w, ${BRAND.logo} 640w`}
              sizes={rail ? "52px" : "132px"}
              alt={`${BRAND.name} staff portal`}
              width={logoWidth(251)}
              height="251"
            />
          </a>
          {isMobile && (
            <button ref={closeButton} type="button" className="icon-btn icon-btn--dark" aria-label="Close menu" onClick={() => { setDrawerOpen(false); menuButton.current?.focus(); }}>
              <Icon name="close" size={24} />
            </button>
          )}
        </div>

        <nav className="side__nav" aria-label="Main">
          <ul>
            {ROUTE_KEYS.filter((r) => allowedFor(r, profile?.role) && (r !== "#/my" || linked)).map((r) => {
              const def: RouteDef = ROUTES[r];
              const n = def.count ? counts[def.count] : undefined;
              return (
                <li key={r}>
                  <a href={r} className="navlink" aria-current={r === route ? "page" : undefined} title={rail ? def.label : undefined}>
                    <Icon name={def.icon} size={20} className="navlink__icon" />
                    <span className="navlink__label">
                      {def.label}
                      {def.hint && <small className="navlink__hint">{def.hint}</small>}
                    </span>
                    {def.count && n !== undefined && n > 0 && (
                      <span className={`badge badge--${def.count === "missingTickets" ? "primary" : "accent"}`}>
                        <span aria-hidden="true">{n}</span>
                        <span className="sr-only">, {COUNT_LABEL[def.count]?.(n) ?? n}</span>
                      </span>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="side__foot">
          <div className="who" title={rail ? `${name}, ${user.email}` : undefined}>
            <span className="who__avatar" aria-hidden="true">{initials(name)}</span>
            <span className="who__text">
              <span className="who__label">Signed in as</span>
              <span className="who__name">{name}</span>
              {profile?.full_name && <span className="who__email">{user.email}</span>}
              {profile?.role && <span className="who__role">{humanise(profile.role)}</span>}
            </span>
          </div>
          <ReportButton className="side__btn" labelClass="side__btn-label" title={rail ? "Something's not working" : undefined} />
          <button type="button" className="side__btn" onClick={() => void signOut()} title={rail ? "Sign out" : undefined}>
            <Icon name="logout" size={20} />
            <span className="side__btn-label">Sign out</span>
          </button>
          {!isMobile && (
            <button type="button" className="side__btn side__btn--quiet" onClick={toggleRail} aria-pressed={rail} title={rail ? "Expand menu" : undefined}>
              <Icon name={rail ? "expand" : "collapse"} size={20} />
              <span className="side__btn-label">Collapse menu</span>
            </button>
          )}
        </div>
      </aside>

      {isMobile && drawerOpen && <div className="scrim" aria-hidden="true" onClick={() => setDrawerOpen(false)} />}

      <main id="main" tabIndex={-1} className="main">
        {linked && route === "#/today" && <MyShiftsLink />}
        <Screen />
      </main>
      <HelpHost />
    </div>
  );
}

function initials(s: string): string {
  const parts = s.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "A";
}
